import { useMemo } from 'react'
import { Activity } from '../../../shared/queries/useGetActivitiesQuery'
import { ActivityDateValue, resolveActivityTimelineDates } from '../../../shared/utils/resolveActivityTimelineDates'
import { ActivityRow } from './ActivityTable.types'

const toDate = (value: ActivityDateValue) => {
    if (!value) return null

    const date = value instanceof Date ? value : new Date(value)

    return Number.isNaN(date.getTime()) ? null : date
}

const toAmount = (value?: string | number | null) => {
    const amount = typeof value === 'number' ? value : parseFloat(value ?? '')

    return Number.isNaN(amount) ? null : amount
}

const formatDate = (date: Date | null) =>
    date ? date.toLocaleDateString('en-US', { timeZone: 'UTC', month: '2-digit', day: '2-digit', year: 'numeric' }) : ''

const toTimelineLabel = (start: Date | null, end: Date | null) => {
    if (!start && !end) return ''
    if (start && end) return `${formatDate(start)} - ${formatDate(end)}`

    return formatDate(start ?? end)
}

const toActivityRow = (activity: Activity): ActivityRow => {
    const { resolvedStartDate, resolvedEndDate, endDateType } = resolveActivityTimelineDates(activity)
    const completionDate = toDate(resolvedEndDate)

    return {
        ...activity,
        completionDate,
        isPlannedCompletionDate: endDateType === 'planned',
        activityTimelineLabel: toTimelineLabel(toDate(resolvedStartDate), completionDate),
        startDateValue: toDate(activity.startDate),
        endDateValue: toDate(activity.endDate),
        plannedStartDateValue: toDate(activity.plannedStartDate),
        plannedEndDateValue: toDate(activity.plannedEndDate),
        studyReportDateValue: toDate(activity.studyReportDate),
        terminationDateValue: toDate(activity.terminationDate),
        resultsAvailableDateValue: toDate(activity.resultsAvailableForExternalUseDate),
        lastSyncedAtValue: toDate(activity.lastSyncedAt),
        createdAtValue: toDate(activity.createdAt),
        updatedAtValue: toDate(activity.updatedAt),
        budgetAmount: toAmount(activity.budget),
        linkedValueMessageCount: activity.linkedValueMessages?.length ?? 0,
        linkedEvidenceCount: activity.linkedEvidence?.length ?? 0,
        linkedStrategicImperativeCount: activity.linkedStrategicImperatives?.length ?? 0,
        linkedTppCellCount: activity.linkedTppCells?.length ?? 0,
        deliverableCount: activity.deliverables?.length ?? 0,
        activityDependencyCount: activity.activityDependencies?.length ?? 0,
    }
}

const byStoredOrder = (left: ActivityRow, right: ActivityRow) => (left.order ?? 0) - (right.order ?? 0)

export const useActivityRows = (activities: Activity[] | undefined) =>
    useMemo(() => (activities ?? []).map(toActivityRow).sort(byStoredOrder), [activities])
